import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ShoppingCart as ShoppingCartIcon, Package, ArrowLeft,
  Truck, Shield, RotateCcw,
} from "lucide-react";
import { Button } from "../comp/ui/button";
import { Badge } from "../comp/ui/badge";
import { Separator } from "../comp/ui/separator";
import { toast } from "sonner";
import { CatalogItem } from "../types/catalog";
import { catalogClient } from "../services/grpc";
import * as pb from "../generated/catalog_pb";
import Cookies from "js-cookie";
import { ImageWithFallback } from "../comp/figma/ImageWithFallback";
import { Analogs } from "../comp/Analogs";
import { CartItem, ShoppingCart } from "../comp/ShoppingCart";
import { Header } from "../comp/Header";
import { AnalogsProductPage } from "../comp/AnalogProductPage";
import { NewHeader } from '../comp/NewHeader';
import { Footer } from '../comp/Footer';
interface ProductProperty {
  name: string;
  value: string;
  code: string;
}

interface StorageInfo {
  storage: string;
  price: number;
  qty: number;
}

interface ExtendedProduct {
  id: number;
  code: string;
  name: string;
  desc: string;
  title: string;
  pic: string;
  price: number;
  qty: number;
  storage: StorageInfo[];
  category: string;
  analogs: string[];
  proplist: ProductProperty[];
}

export function ProductDetail() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [product, setProduct] = useState<ExtendedProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [analogs,setAnalogs]=useState<string[]>([]);
  const [isInCartStorage, setIsInCartStorage] = useState<string[]>([]);
  // ── Init GUID ──
  useEffect(() => {
    const existingGuid = Cookies.get("guid");
    if (typeof existingGuid !== "string") {
      const req = new pb.guidReq();
      req.setIp(window.location.hostname);
      catalogClient.getGuid(req, {}, (err, response) => {
        if (err) {
          console.error("Error fetching guid:", err.code, err.message);
        } else {
          Cookies.set("guid", response.getGuid());
        }
      });
    }
  }, []);

  // ── Fetch product ──
  useEffect(() => {
    const productCode = id?.trim() || "";
    if (productCode) fetchProduct(productCode);
  }, [id]);

  const fetchProduct = async (productCode: string) => {
    setLoading(true);
    try {
      const req = new pb.ItembyCodeReq();
      req.setCode(productCode);

      const response = await new Promise<any>((resolve, reject) => {
        catalogClient.getItemByCode(req, {}, (err, res) => {
          if (err) reject(err);
          else resolve(res);
        });
      });

      const items= response.toObject();
    const storage: StorageInfo[] = (items.storagelistList || []).map((p: any) => ({
    storage: p.storage,
    price: p.price,
    qty: p.qty,
  }));
const props: ProductProperty[] = (items.props.propList||[]).map((p: any)=>({
 code: p.code,
          name: p.name,
          value: p.value
}));
      const propMap: Record<string, ProductProperty> = {};
      for (const p of props) {
        propMap[p.code] = p;
      }

      const category = propMap["category"]?.value || "";
      const analog = propMap["analog"]?.value || "";
      const analogs = analog.split("/").filter((a) => a.trim() !== "");
      const allAnalogs = analogs.filter(code => code !== items.code);
      setAnalogs(allAnalogs);
      const excludeKeys = ["analog"];
      const proplist = Object.values(propMap).filter(
        (p) => !excludeKeys.includes(p.code)
      );

      setProduct({
        id: items.id,
        code: items.code,
        name: items.name,
        desc: items.desc,
        title: items.title,
        pic: items.pic,
        price: items.price,
        qty: items.qty,
        storage,
        category,
        analogs,
        proplist,
      });
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Не удалось загрузить товар");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════
  //  Cart functions — ALL at component level
  // ═══════════════════════════════════════
  const handleAddToCart = async (storage: string, price: number) => {
    if (!product) return;

    try {
      const guid = Cookies.get("guid");
      if (typeof guid !== "string") {
        toast.error("Сессия не инициализирована");
        return;
      }

      const req = new pb.CartAddReq();
      req.setGuid(guid);
      req.setProductId(product.id);
      req.setQty(1);
      req.setStorage(storage);
      catalogClient.cartAdd(req, {}, (err, _res) => {
        if (err) console.error("Error adding to cart:", err.code, err.message);
      });

      setCartItems((prev) => {
        const safe = prev || [];
        const existing = safe.find(
          (item) => item.product.id === product.id && item.storage === storage
        );
        if (existing) {
          return safe.map((item) =>
            item.product.id === product.id && item.storage === storage
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        }
        return [
          ...safe,
          {
            product: product as any,
            quantity: 1,
            storage,
            price,
          },
        ];
      });
      setIsInCartStorage(prev => [...prev, `${product.id}_${storage}`]);
      toast.success(`${product.name} добавлен в корзину`);
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Не удалось добавить в корзину");
    }
  };

  const updateQuantity = (
    productId: number,
    quantity: number,
    storage: string
  ) => {
    if (quantity === 0) {
      removeFromCart(productId, storage);
      return;
    }
    try {
      const guid = Cookies.get("guid");
      if (typeof guid === "string") {
        const req = new pb.CartAddReq();
        req.setGuid(guid);
        req.setProductId(productId);
        req.setQty(quantity);
        req.setStorage(storage);
        catalogClient.cartUpdate(req, {}, (err, _res) => {
          if (err) console.error("Error updating cart:", err.code, err.message);
        });
      }
      setCartItems((prev) =>
        (prev || []).map((item) =>
          item.product.id === productId && item.storage === storage
            ? { ...item, quantity,storage }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const removeFromCart = (productId: number, storage: string) => {
    try {
      const guid = Cookies.get("guid");
      if (typeof guid === "string") {
        const req = new pb.CartRemoveReq();
        req.setGuid(guid);
        req.setProductId(productId);
        req.setQty(0);
        req.setStorage(storage);
        catalogClient.cartRemove(req, {}, (err, _res) => {
          if (err)
            console.error("Error removing from cart:", err.code, err.message);
        });
      }
      setCartItems((prev) =>
        (prev || []).filter(
          (item) =>
            !(item.product.id === productId && item.storage === storage)
        )
      );
      setIsInCartStorage(prev => prev.filter(s => s !== `${productId}_${storage}`));
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };
const setOrder = ()=>{
              if (!customerName || !customerPhone) {
                setAlertMessage(`Пожалуйста. Введите Ваше имя и номер телефона для связи`);
                return;
            }
          
              const guid = Cookies.get('guid');
              if (typeof guid === 'string') {
                const req=new pb.OrderAcceptReq();
                req.setGuid(guid);
                req.setName(customerName);
                req.setPhone(customerPhone);
                catalogClient.orderAccept(req, {}, (err, response) => {
                  if (err) {
                    console.error('Error removing from cart:', err.code, err.message);
                  } else {
                  if (response.getStatus()=="fail") { 
                    setAlertMessage(`${response.getMessage()}`);
                  } else {
                    setAlertMessage(`Спасибо за заказ.\n\ 
                      В ближайшее время наш менеджер свяжется с Вами.\n\ 
                      Номер вашего заказа: ${response.getNum()}`);
                    setCartItems([]);
                    setIsCartOpen(false);
                  }
                    return;
                  }
                });
              }
            }
             const OnsetIsInCartStorage=(items:string[])=>{
    setIsInCartStorage(items);
  }
  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-gray-300 rounded mb-8" />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-square bg-gray-300 rounded-lg" />
              <div className="space-y-4">
                <div className="h-10 bg-gray-300 rounded" />
                <div className="h-6 bg-gray-300 rounded w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ──
  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
        <div className="container mx-auto px-4 text-center">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-blue-900 mb-4">
            Товар не найден
          </h1>
          <Button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Назад
          </Button>
        </div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white"> 
      <NewHeader
        cartItemCount={cartItemCount}
        onCartClick={() => setIsCartOpen(true)}
        onAccountClick={() => setIsAccountOpen(true)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery} page={"product"}                
              />
      <div className="container mx-auto px-4">
           
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Link to="/catalog/all" className="hover:text-blue-600">
            Главная
          </Link>
          <span>/</span>
          <Link to="/catalog/all" className="hover:text-blue-600">
            Каталог
          </Link>
          <span>/</span>
          <span className="text-blue-900 font-medium">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Image */}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg bg-white shadow-lg border border-blue-100">
              <ImageWithFallback
                src={`https://cdn.yutongoriginal.ru/product_images/${product.code}.jpg`}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

 
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              <Badge className="mb-3 bg-blue-100 text-blue-700">
                {product.category}
              </Badge>
              <h1 className="text-3xl font-bold text-blue-900 mb-2">
                {product.name}
              </h1>
              <p className="text-gray-500 text-sm">
                Артикул: {product.code}
              </p>
            </div>

            <Separator />

            {product.desc && (
              <p className="text-gray-700 leading-relaxed">{product.desc}</p>
            )}

            <Separator />
                         {product.proplist.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <h4 className="font-semibold text-blue-900 mb-2">
                  Характеристики
                </h4>
                <dl className="space-y-1 text-sm">
                  {product.proplist.map((prop) => (
                    <div
                      key={prop.code}
                      className="flex justify-between py-1 border-b last:border-0"
                    >
                      <dt className="text-gray-600">{prop.name}: </dt>
                      <dd className="font-medium pl-3"> {prop.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {/* Storage / Price / Add to Cart */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
              {product.storage.map((stock, idx) => (
                <div
                  key={`${stock.storage}-${idx}`}
                  className="flex items-center justify-between gap-4 pt-3 border-t border-gray-200 first:border-0 first:pt-0"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span>Склад {stock.storage}</span>
                    <Package className="h-4 w-4 text-gray-500" />
                    <span
                      className={
                        stock.qty > 0
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {stock.qty > 0 ? `${stock.qty} шт.` : "под заказ"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xl font-bold text-blue-600">
                      {stock.price.toFixed(2)}₽
                    </div>
                    <Button
                      className={isInCartStorage.includes(`${product.id}_${stock.storage}`)?"bg-gradient-to-br from-green-700 to-green-500": "bg-gradient-to-br from-blue-950 to-blue-800"} 
                      onClick={() =>
                        handleAddToCart(stock.storage, stock.price)
                      }
                      disabled={stock.qty === 0}
                    >
                      <ShoppingCartIcon className="mr-2 h-4 w-4" />
                      В корзину
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Продолжить покупки
            </Button>

            <AnalogsProductPage analoglists={analogs} onAddToCart={function (product: CatalogItem): void {
              throw new Error("Function not implemented.");
            } } onViewDetails={function (product: CatalogItem): void {
              throw new Error("Function not implemented.");
            } } productcode={product.code}/>
            <Separator />

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <Truck className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900">Доставка</p>
                  <p className="text-sm text-blue-700">По всей России</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900">Гарантия</p>
                  <p className="text-sm text-blue-700">Официальная</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <RotateCcw className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900">Возврат</p>
                  <p className="text-sm text-blue-700">14 дней</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <Package className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900">Качество</p>
                  <p className="text-sm text-blue-700">Оригинал</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer/>
       {Array.isArray(cartItems) && (
                                <ShoppingCart
                                  isOpen={isCartOpen}
                                  onOpenChange={setIsCartOpen}
                                  cartItems={cartItems}
                                  onUpdateQuantity={updateQuantity}
                                  onRemoveItem={removeFromCart}
                                  onOrder={setOrder}
                                  customerName={customerName}
                                  customerPhone={customerPhone}
                                  setCustomerName={setCustomerName}
                                  setCustomerPhone={setCustomerPhone}
                                  setCartItemCount={setCartItemCount}
                                  setIsInCartStorage={OnsetIsInCartStorage}
                                />
                              )}
    </div>
  );
}
